import { useCallback, useEffect, useMemo, useState } from 'react';

import { AddressService } from '../../../services/address';
import { getUsers } from '../../../services/estate';
import { maskCpf, maskPhoneNumber } from '../../../services/masks';
import { dangerNotification } from '../../../services/notification';
import { UserState } from '../../../types/UserState';
import { initialCollaboratorActivationForm } from '../constants';
import {
  ActivationStep,
  CollaboratorActivationFormState,
  CommissioningCollaborator,
  UpdateActivationFormFieldFn
} from '../types';
import { applyRecipientTypeChange } from '../utils/applyRecipientTypeChange';
import {
  getNextActivationStep,
  getPreviousActivationStep
} from '../utils/activationStepNavigation';
import { buildPagarmeActivationPayload } from '../utils/buildPagarmeActivationPayload';
import { mapActivationPayloadToForm } from '../utils/mapActivationPayloadToForm';
import {
  getLastActivationStep,
  validateActivationStep
} from '../utils/validateActivationStep';
import { normalizePagarmeBankHolderName } from '../utils/activationFormHelpers';
import {
  ActivationDraftScope,
  clearActivationDraft,
  readActivationDraft,
  writeActivationDraft
} from '../utils/activationDraftStorage';

type DropdownOption = {
  key: string | number;
  text: string;
  value: string;
};

type Params = {
  open: boolean;
  mode: 'create' | 'edit';
  isBrokerAdmin: boolean;
  isEstateAdmin: boolean;
  defaultEstateId: string;
  estateOptions: Array<{ key: string; text: string; value: string }>;
  collaborator?: CommissioningCollaborator | null;
};

const collaboratorCreateLatestDraftScope: ActivationDraftScope = {
  type: 'collaborator-create-latest'
};

const getUserDocument = (user: UserState) =>
  String(user.document || (user as UserState & { doc?: string }).doc || '');

const getUserPhone = (user: UserState) => String(user.cellphone || user.phone || '');

const toUserOptions = (users: UserState[]): DropdownOption[] =>
  users.map((user, index) => ({
    key: user.id || index,
    text: user.name || user.email || 'Usuário sem nome',
    value: user.id
  }));

const ensureCurrentUserOption = (
  options: DropdownOption[],
  collaborator?: CommissioningCollaborator | null
): DropdownOption[] => {
  if (!collaborator?.userId || options.some(option => option.value === collaborator.userId)) {
    return options;
  }

  return [
    ...options,
    {
      key: collaborator.userId,
      text: collaborator.name || 'Usuário selecionado',
      value: collaborator.userId
    }
  ];
};


export const useCollaboratorActivationWizard = ({
  open,
  mode,
  isBrokerAdmin,
  isEstateAdmin,
  defaultEstateId,
  collaborator
}: Params) => {
  const [form, setForm] = useState<CollaboratorActivationFormState>(
    initialCollaboratorActivationForm
  );
  const [activationStep, setActivationStep] = useState<ActivationStep>(1);
  const [activationStepError, setActivationStepError] = useState('');
  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState<string | null>(null);
  const [addressAutoFilled, setAddressAutoFilled] = useState<string[]>([]);
  const [users, setUsers] = useState<UserState[]>([]);
  const [userOptions, setUserOptions] = useState<DropdownOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const selectedEstateId = form.estateId || collaborator?.estateId || defaultEstateId;
  const shouldLoadUsers = Boolean(selectedEstateId) && (isBrokerAdmin || isEstateAdmin);

  const draftScope = useMemo(() => {
    if (mode === 'edit' && collaborator?.id) {
      return { type: 'collaborator-edit' as const, collaboratorId: collaborator.id };
    }

    if (selectedEstateId) {
      return { type: 'collaborator-create' as const, estateId: selectedEstateId };
    }

    if (mode === 'create') {
      return collaboratorCreateLatestDraftScope;
    }

    return null;
  }, [collaborator?.id, mode, selectedEstateId]);

  const validationOptions = useMemo(
    () => ({
      mode: 'collaborator' as const,
      requireEstate: isBrokerAdmin
    }),
    [isBrokerAdmin]
  );

  const resetWizard = useCallback(() => {
    const base: CollaboratorActivationFormState = {
      ...initialCollaboratorActivationForm,
      estateId: isEstateAdmin ? defaultEstateId : collaborator?.estateId || ''
    };

    let nextForm = base;
    let nextStep: ActivationStep = 1;

    if (mode === 'edit' && collaborator) {
      nextForm = mapActivationPayloadToForm(
        {
          recipientType: collaborator.activation?.recipientType as
            | CollaboratorActivationFormState['recipientType']
            | undefined,
          ...collaborator.activation
        },
        {
          ...base,
          estateId: collaborator.estateId,
          userId: collaborator.userId || ''
        }
      );
    }

    if (draftScope) {
      const draft = readActivationDraft<CollaboratorActivationFormState>(draftScope);
      const persistedUpdatedAt = collaborator?.activation?.updatedAt ||
        collaborator?.activation?.completedAt ||
        '';
      const draftIsNewerThanPersisted = persistedUpdatedAt
        ? new Date(draft?.updatedAt || 0).getTime() > new Date(persistedUpdatedAt).getTime()
        : true;

      if (draft && (mode === 'create' || draftIsNewerThanPersisted)) {
        nextForm = { ...nextForm, ...draft.form };
        nextStep = draft.step;
      }
    }

    setForm(nextForm);
    setActivationStep(nextStep);
    setActivationStepError('');
    setAddressLookupError(null);
    setAddressAutoFilled([]);
  }, [collaborator, defaultEstateId, draftScope, isEstateAdmin, mode]);

  useEffect(() => {
    if (!open) return;
    resetWizard();
  }, [open, resetWizard]);

  useEffect(() => {
    if (!open || !draftScope) return;

    const draft = {
      step: activationStep,
      form
    };

    writeActivationDraft(draftScope, draft);
    if (mode === 'create' && draftScope.type === 'collaborator-create') {
      writeActivationDraft(collaboratorCreateLatestDraftScope, draft);
    }
  }, [activationStep, draftScope, form, mode, open]);

  useEffect(() => {
    if (!open || !shouldLoadUsers) {
      setUsers([]);
      setUserOptions([]);
      return;
    }

    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const usersResponse = await getUsers(selectedEstateId);
        const loadedUsers = usersResponse || [];
        setUsers(loadedUsers);
        setUserOptions(ensureCurrentUserOption(toUserOptions(loadedUsers), collaborator));
      } catch (error) {
        setUsers([]);
        setUserOptions([]);
        dangerNotification('Erro', 'Não foi possível carregar usuários da imobiliária.');
      } finally {
        setUsersLoading(false);
      }
    };

    loadUsers();
  }, [collaborator, open, selectedEstateId, shouldLoadUsers]);

  const updateFormField: UpdateActivationFormFieldFn = (field, value) => {
    setForm(prev => {
      if (field === 'recipientType') {
        return applyRecipientTypeChange(
          prev,
          value as CollaboratorActivationFormState['recipientType']
        );
      }

      if (field === 'document' && prev.recipientType === 'corporation') {
        const nextDocument = value as string;
        return {
          ...prev,
          document: nextDocument,
          bankHolderType: 'company',
          bankHolderDocument: nextDocument,
          bankHolderName: normalizePagarmeBankHolderName(
            prev.companyName || prev.tradingName || prev.bankHolderName
          )
        };
      }

      return { ...prev, [field]: value };
    });
    setActivationStepError('');
  };

  const setEstateId = (estateId: string) => {
    setForm(prev => ({
      ...prev,
      estateId,
      userId: ''
    }));
    setUserOptions([]);
    setUsers([]);
    setActivationStepError('');
  };

  const applySelectedUser = (userId: string) => {
    const selectedUser = users.find(user => user.id === userId);

    setForm(prev => {
      const next: CollaboratorActivationFormState = {
        ...prev,
        userId
      };

      if (!selectedUser) {
        return { ...next, userId: '' };
      }

      if (prev.recipientType === 'individual') {
        return {
          ...next,
          name: selectedUser.name || prev.name,
          email: selectedUser.email || prev.email,
          document: selectedUser ? maskCpf(getUserDocument(selectedUser)) : prev.document,
          phone: selectedUser ? maskPhoneNumber(getUserPhone(selectedUser)) : prev.phone,
          bankHolderName: normalizePagarmeBankHolderName(
            selectedUser.name || prev.bankHolderName
          ),
          bankHolderDocument: selectedUser
            ? maskCpf(getUserDocument(selectedUser))
            : prev.bankHolderDocument
        };
      }

      return {
        ...next,
        name: selectedUser.name || prev.name,
        email: selectedUser.email || prev.email,
        phone: selectedUser ? maskPhoneNumber(getUserPhone(selectedUser)) : prev.phone
      };
    });
    setActivationStepError('');
  };

  const handleZipCodeLookup = async (zipCodeRaw: string) => {
    const cleanZip = zipCodeRaw.replace(/\D/g, '');
    if (cleanZip.length !== 8) return;

    setAddressLookupLoading(true);
    setAddressLookupError(null);
    setAddressAutoFilled([]);
    try {
      const response = await AddressService.getAddressByZipCode(cleanZip);
      const filled: string[] = [];

      setForm(prev => {
        const next = { ...prev };
        if (response.street) {
          next.street = response.street;
          filled.push('street');
        }
        if ((response as { district?: string }).district) {
          next.neighborhood = (response as { district?: string }).district || '';
          filled.push('neighborhood');
        }
        if (response.city) {
          next.city = response.city;
          filled.push('city');
        }
        if (response.state) {
          next.state = String(response.state).toUpperCase();
          filled.push('state');
        }
        return next;
      });

      setAddressAutoFilled(filled);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error || '');
      setAddressLookupError(msg || 'CEP não encontrado. Verifique e tente novamente.');
    } finally {
      setAddressLookupLoading(false);
    }
  };

  const handleNextStep = () => {
    const validationError = validateActivationStep(activationStep, form, validationOptions);
    if (validationError) {
      setActivationStepError(validationError);
      return;
    }

    setActivationStepError('');
    const nextStep = getNextActivationStep(activationStep, form.recipientType);
    if (nextStep) {
      setActivationStep(nextStep);
    }
  };

  const handleBackStep = (onClose: () => void) => {
    setActivationStepError('');
    if (activationStep <= 1) {
      onClose();
      return;
    }

    const previousStep = getPreviousActivationStep(activationStep, form.recipientType);
    if (previousStep) {
      setActivationStep(previousStep);
    }
  };

  const validateAllSteps = (): boolean => {
    const lastStep = getLastActivationStep();
    for (let step = 1; step <= lastStep; step += 1) {
      const validationError = validateActivationStep(
        step as ActivationStep,
        form,
        validationOptions
      );
      if (validationError) {
        setActivationStep(step as ActivationStep);
        setActivationStepError(validationError);
        return false;
      }
    }
    return true;
  };

  const clearDraft = () => {
    if (!draftScope) return;
    clearActivationDraft(draftScope);
    if (mode === 'create') {
      clearActivationDraft(collaboratorCreateLatestDraftScope);
    }
  };

  const buildPayload = () => buildPagarmeActivationPayload(form);

  return {
    form,
    activationStep,
    activationStepError,
    addressLookupLoading,
    addressLookupError,
    addressAutoFilled,
    userOptions,
    usersLoading,
    selectedEstateId,
    lastActivationStep: getLastActivationStep(),
    updateFormField,
    setEstateId,
    applySelectedUser,
    handleZipCodeLookup,
    handleNextStep,
    handleBackStep,
    validateAllSteps,
    buildPayload,
    clearDraft
  };
};
